package com.example.positionahead.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.example.positionahead.domain.DiscoverAnalyzer
import com.example.positionahead.model.DiscoverAssetClass
import com.example.positionahead.model.DiscoverIdea
import com.example.positionahead.model.DiscoverSection
import com.example.positionahead.ui.components.CollapsibleSection
import com.example.positionahead.ui.theme.gainColor
import com.example.positionahead.ui.theme.lossColor
import com.example.positionahead.util.formatCurrency
import com.example.positionahead.util.formatPercent

import com.example.positionahead.model.Quote
import com.example.positionahead.model.SymbolPerformance
import com.example.positionahead.model.SymbolPriceHistory

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DiscoverScreen(
    discoverSections: List<DiscoverSection>,
    watchlistSymbols: Set<String>,
    onWatch: (String) -> Unit,
    onExplore: (String) -> Unit,
    chartSymbol: String,
    chartRangeDays: Int,
    chartHistory: SymbolPriceHistory?,
    chartQuote: Quote?,
    chartPerformance: SymbolPerformance?,
    symbolSuggestions: List<String>,
    onSymbolChange: (String) -> Unit,
    onRangeChange: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    val sectionExpanded = remember {
        mutableStateMapOf<String, Boolean>().apply {
            discoverSections.forEach { put(it.kind.name, it.kind.name == "FOR_YOU") }
        }
    }
    var showResearch by remember { mutableStateOf(false) }

    Column(modifier = modifier.fillMaxSize()) {
        TopAppBar(title = { Text("Discover") })

        LazyColumn(
            contentPadding = PaddingValues(bottom = 32.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            item {
                PaddingWrapper {
                    Text(
                        text = "Build a portfolio that matches your financial plan. ${DiscoverAnalyzer.DISCLAIMER}",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                    )
                }
            }

            items(discoverSections, key = { it.kind.name }) { section ->
                PaddingWrapper {
                    CollapsibleSection(
                        title = section.title,
                        expanded = sectionExpanded[section.kind.name] == true,
                        onToggle = {
                            sectionExpanded[section.kind.name] = !(sectionExpanded[section.kind.name] == true)
                        },
                    ) {
                        section.ideas.forEach { idea ->
                            DiscoverIdeaRow(
                                idea = idea,
                                isWatched = idea.symbol.uppercase() in watchlistSymbols,
                                onWatch = onWatch,
                                onExplore = onExplore,
                            )
                        }
                    }
                }
            }

            item {
                PaddingWrapper {
                    HorizontalDivider()
                    CollapsibleSection(
                        title = "Market Research",
                        expanded = showResearch,
                        onToggle = { showResearch = !showResearch }
                    ) {
                        MarketChartScreen(
                            chartSymbol = chartSymbol,
                            chartRangeDays = chartRangeDays,
                            chartHistory = chartHistory,
                            chartQuote = chartQuote,
                            chartPerformance = chartPerformance,
                            symbolSuggestions = symbolSuggestions,
                            onSymbolChange = onSymbolChange,
                            onRangeChange = onRangeChange,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PaddingWrapper(content: @Composable () -> Unit) {
    Column(modifier = Modifier.padding(horizontal = 16.dp)) {
        content()
    }
}

@Composable
private fun DiscoverIdeaRow(
    idea: DiscoverIdea,
    isWatched: Boolean,
    onWatch: (String) -> Unit,
    onExplore: (String) -> Unit,
) {
    val changeColor = if (idea.changePercent >= 0) gainColor else lossColor
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 4.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top,
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            text = idea.symbol,
                            style = MaterialTheme.typography.titleSmall,
                            fontWeight = FontWeight.Bold,
                        )
                        Text(
                            text = idea.assetClass.label(),
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.primary,
                        )
                    }
                    if (idea.name.isNotBlank() && idea.name != idea.symbol) {
                        Text(
                            text = idea.name,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.75f),
                        )
                    }
                }
                Column(horizontalAlignment = Alignment.End) {
                    Text(
                        text = formatCurrency(idea.price),
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.SemiBold,
                    )
                    Text(
                        text = formatPercent(idea.changePercent),
                        style = MaterialTheme.typography.bodySmall,
                        color = changeColor,
                    )
                }
            }

            if (idea.researchSummary.isNotBlank()) {
                Text(
                    text = idea.researchSummary,
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.primary,
                )
            }

            Text(
                text = idea.whyConsider,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.85f),
            )

            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedButton(
                    onClick = { onWatch(idea.symbol) },
                    enabled = !isWatched,
                ) {
                    Text(if (isWatched) "Watching" else "Watch")
                }
                TextButton(onClick = { onExplore(idea.symbol) }) {
                    Text("Add")
                }
            }
        }
    }
}

private fun DiscoverAssetClass.label(): String = when (this) {
    DiscoverAssetClass.STOCK -> "Stock"
    DiscoverAssetClass.ETF -> "ETF"
    DiscoverAssetClass.BOND -> "Bond"
    DiscoverAssetClass.MUTUAL_FUND -> "Mutual fund"
}
